import { defineStore, storeToRefs } from 'pinia'
import http from './http'
import { ref, watch } from 'vue'
import { Response } from '@/types'
import { RouteOption } from '@/router/interface'
import { replaceRoutes, removeRoute } from '@/router/dynamicRoutes'
import { useSettingStore } from './setting'
import { RouteRecordRaw, RouteMeta } from 'vue-router'
import { useAuthStore } from '@/plugins'
import router from '@/router'
import { useLoadingStore } from '@/store'
import i18n from '@/locales'

export interface MenuProps {
  id?: number
  name: string
  path: string
  title?: string
  icon?: string
  badge?: number | string
  target?: '_self' | '_blank'
  link?: string
  component: string
  renderMenu?: boolean
  permission?: string
  parent?: string
  children?: MenuProps[]
  cacheable?: boolean
  view?: string
  sort?: number
}

/**
 * 过滤菜单
 * @param routes
 * @param parentPermission
 */
function doMenuFilter(routes: Readonly<RouteRecordRaw[]>, parentPermission?: string) {
  const { hasAuthority } = useAuthStore()

  const setCache = (meta: RouteMeta) => {
    meta._cache = {
      renderMenu: meta.renderMenu
    }
  }

  routes.forEach((route) => {
    const required = route.meta?.permission ?? parentPermission
    // if (route.meta?.renderMenu === undefined && required) {
    if (required) {
      route.meta = route.meta ?? {}
      setCache(route.meta)
      route.meta.renderMenu = hasAuthority(route.meta.permission)
    }
    if (route.children) {
      doMenuFilter(route.children, required)
    }
  })
}

/**
 * 重置过滤
 * @param routes
 */
function resetMenuFilter(routes: Readonly<RouteRecordRaw[]>) {
  const resetCache = (meta: RouteMeta) => {
    if (meta._cache) {
      meta.renderMenu = meta._cache?.renderMenu
    }
    delete meta._cache
  }
  routes.forEach((route) => {
    if (route.meta) {
      resetCache(route.meta)
    }
    if (route.children) {
      resetMenuFilter(route.children)
    }
  })
}

// 菜单数据转为路由数据
const toRoutes = (list: MenuProps[]): RouteOption[] => {
  return list.map((item) => ({
    name: item.name,
    path: item.path,
    component: item.component,
    children: item.children && toRoutes(item.children),
    meta: {
      title: i18n.global.te(`menu.${item.name}`) ? i18n.global.t(`menu.${item.name}`) : item.title,
      permission: item.permission,
      icon: item.icon,
      renderMenu: item.renderMenu,
      cacheable: item.cacheable,
      href: item.link,
      badge: /^(false|true)$/i.test(item.badge + '') ? JSON.parse(item.badge + '') : item.badge,
      target: item.target,
      view: item.view
    }
  }))
}

export const useMenuStore = defineStore('menu', () => {
  const menuList = ref<MenuProps[]>([])
  const authMenuList = ref<MenuProps[]>([])

  const pagination = ref({
    current: 1,
    pageSize: 10,
    totalPage: undefined,
    total: undefined,
    pageSizeOptions: ['10', '20', '30', '40', '50']
  })

  const searchParams = ref({
    parentId: undefined as number | undefined,
    title: undefined as string | undefined
  })

  const handleTableChange = (page: any) => {
    pagination.value.current = page.current
    pagination.value.pageSize = page.pageSize
    pagination.value.total = page.total
    getAuthMenuList()
  }

  const { filterMenu } = storeToRefs(useSettingStore())

  const checkMenuPermission = () => {
    if (filterMenu.value) {
      doMenuFilter(router.options.routes)
    } else {
      resetMenuFilter(router.options.routes)
    }
  }

  checkMenuPermission()

  watch(filterMenu, checkMenuPermission)

  async function getMenuList() {
    const { setPageLoading } = useLoadingStore()
    setPageLoading(true)
    return http
      .request<MenuProps[], Response<MenuProps[]>>('/admin/menu', 'GET')
      .then((res) => {
        const { data } = res
        console.log(data)
        menuList.value = data
        replaceRoutes(toRoutes(data), false)
        checkMenuPermission()
        return data
      })
      .finally(() => setPageLoading(false))
  }

  async function getAuthMenuList() {
    return http
      .request<any>('/menu/list', 'GET', {
        current: pagination.value.current,
        pageSize: pagination.value.pageSize,
        parentId: searchParams.value.parentId,
        title: searchParams.value.title
      })
      .then((res) => {
        const { data } = res
        authMenuList.value = data.list
        pagination.value.total = data.total
        pagination.value.totalPage = data.totalPage
        pagination.value.current = data.current
        pagination.value.pageSize = data.pageSize
        return data
      })
  }

  function resetMenu() {
    menuList.value = []
  }

  function resetSearchParams() {
    searchParams.value.parentId = undefined
    searchParams.value.title = undefined
  }

  function refreshMenuTranslations() {
    // Re-translate menu titles when locale changes
    const updateRouteTitles = (routes: readonly RouteRecordRaw[]) => {
      routes.forEach((route) => {
        if (route.name && route.meta) {
          const name = route.name as string
          route.meta.title = i18n.global.te(`menu.${name}`) ? i18n.global.t(`menu.${name}`) : route.meta.title
        }
        if (route.children) {
          updateRouteTitles(route.children)
        }
      })
    }
    updateRouteTitles(router.options.routes)
  }

  function updateSearchParams(params: { parentId?: number; title?: string }) {
    if (params.parentId !== undefined) {
      searchParams.value.parentId = params.parentId
    }
    if (params.title !== undefined) {
      searchParams.value.title = params.title
    }
    pagination.value.current = 1
    getAuthMenuList()
  }

  async function addMenu(menu: MenuProps) {
    return http
      .request<any, Response<any>>('/menu/create', 'POST_JSON', menu)
      .then((res) => {
        return res.data
      })
      .finally(getAuthMenuList)
  }

  async function updateMenu(menu: MenuProps) {
    return http
      .request<any, Response<any>>(`/menu/update/${menu.id}`, 'POST_JSON', menu)
      .then((res) => {
        return res.data
      })
      .finally(getAuthMenuList)
  }

  async function removeMenu(id: number) {
    return http
      .request<any, Response<any>>('/menu/delete', 'GET', { id })
      .then(async (res) => {
        if (res.code === 200) {
          removeRoute(res.data.name)
        }
      })
      .finally(getAuthMenuList)
  }

  async function updateMenuHidden(id: number, hidden: number) {
    return http
      .request<any, Response<any>>(`/menu/updateHidden/${id}`, 'POST', { hidden })
      .then((res) => {
        return res.data
      })
      .finally(getAuthMenuList)
  }

  return {
    menuList,
    authMenuList,
    pagination,
    searchParams,
    handleTableChange,
    getMenuList,
    getAuthMenuList,
    addMenu,
    updateMenu,
    removeMenu,
    updateMenuHidden,
    resetMenu,
    resetSearchParams,
    updateSearchParams,
    refreshMenuTranslations
  }
})
